import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSave, FiX, FiBell } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_ENDPOINTS } from '../config/api';
import apiClient, { endpoints } from '../api/client';
import DeleteItemModal from '../components/DeleteItemModal';

const SMSTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const { isSuperAdmin } = useAuth();
  const { showToast } = useToast();
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationWarnings, setValidationWarnings] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    body_text: ''
  });

  // Allowed template variables for client-side validation
  const ALLOWED_VARIABLES = [
    'first_name', 'last_name', 'email',
    'certificate_type', 'expiry_date',
    'days_left', 'days_until_expiry'
  ];

  const supportedVariables = ALLOWED_VARIABLES.map(variable => `{{${variable}}}`);

  // Client-side validation functions
  const validateTemplateVariables = (text) => {
    if (!text) return { invalidVars: [], suspiciousPatterns: [] };

    const variables = text.match(/{{\s*([^}]+)\s*}}/g) || [];
    const invalidVars = [];
    const suspiciousPatterns = [];

    variables.forEach(variable => {
      const varName = variable.replace(/[{}\s]/g, '').toLowerCase();

      if (!ALLOWED_VARIABLES.includes(varName)) {
        invalidVars.push(varName);
      }

      if (/[<>"'`\\]/.test(varName) || varName.includes('script') || varName.includes('javascript')) {
        suspiciousPatterns.push(varName);
      }
    });

    return { invalidVars, suspiciousPatterns };
  };

  const validateTemplateName = (name) => {
    if (!name || typeof name !== 'string') {
      return { valid: false, error: 'Template name is required' };
    }

    if (name.length < 3 || name.length > 100) {
      return { valid: false, error: 'Template name must be between 3 and 100 characters' };
    }

    if (!/^[a-zA-Z0-9_\-\s]+$/.test(name)) {
      return { valid: false, error: 'Template name can only contain letters, numbers, underscores, hyphens, and spaces' };
    }

    return { valid: true };
  };

  const validateSMSLength = (message) => {
    const length = message ? message.length : 0;
    if (length > 160) {
      return { warning: `Message is ${length} characters. Consider splitting into multiple messages.` };
    }
    return {};
  };

  const validateFormData = (data) => {
    const errors = {};
    const warnings = {};

    // Validate name
    const nameValidation = validateTemplateName(data.name);
    if (!nameValidation.valid) {
      errors.name = nameValidation.error;
    }

    // Validate message variables
    const messageValidation = validateTemplateVariables(data.message);
    if (messageValidation.invalidVars.length > 0) {
      errors.message = `Invalid variables: ${messageValidation.invalidVars.join(', ')}`;
    }
    if (messageValidation.suspiciousPatterns.length > 0) {
      errors.message = `Suspicious patterns detected: ${messageValidation.suspiciousPatterns.join(', ')}`;
    }

    // Validate SMS length
    const lengthValidation = validateSMSLength(data.message);
    if (lengthValidation.warning) {
      warnings.message = lengthValidation.warning;
    }

    return { errors, warnings };
  };

  const sampleData = {
    user_name: 'John Doe',
    certificate_type: 'CPR Certification',
    expiry_date: '2024-12-31',
    days_left: '30'
  };

  const SMS_LIMIT = 160;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${API_ENDPOINTS.SMS_TEMPLATES}?sort_by=created_at&sort_order=ASC`);
      if (response.data.success) {
        setTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching SMS templates:', error);
      showToast('Failed to fetch SMS templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    const validation = validateFormData(formData);
    setValidationErrors(validation.errors);
    setValidationWarnings(validation.warnings);

    if (Object.keys(validation.errors).length > 0) {
      return; // Don't submit if there are validation errors
    }

    // Validate character limit
    if (formData.body_text.length > SMS_LIMIT) {
      alert(`SMS body exceeds ${SMS_LIMIT} character limit. Current: ${formData.body_text.length} characters.`);
      return;
    }

    // Validate variables
    const variableRegex = /{{([^}]+)}}/g;
    const matches = formData.body_text.match(variableRegex) || [];

    for (const match of matches) {
      if (!supportedVariables.includes(match)) {
        alert(`Unsupported variable: ${match}. Supported variables: ${supportedVariables.join(', ')}`);
        return;
      }
    }

    try {
      const url = editingTemplate ? API_ENDPOINTS.SMS_TEMPLATE_BY_ID(editingTemplate.id) : API_ENDPOINTS.SMS_TEMPLATES;
      const method = editingTemplate ? 'put' : 'post';

      const response = await apiClient[method](url, formData);

      if (response.data.success) {
        showToast(
          editingTemplate ? 'SMS template updated successfully' : 'SMS template created successfully',
          'success'
        );
        fetchTemplates();
        resetForm();
        setValidationErrors({});
        setValidationWarnings({});
      }
    } catch (error) {
      console.error('Error saving template:', error);
      // Handle server-side validation errors
      if (error.response?.data?.message) {
        if (error.response.data.message.includes('Invalid variables')) {
          setValidationErrors({ general: error.response.data.message });
        } else if (error.response.data.message.includes('Suspicious patterns')) {
          setValidationErrors({ general: error.response.data.message });
        } else {
          setValidationErrors({ general: error.response.data.message });
        }
      }
      const errorMessage = error.response?.data?.message || 'Failed to save SMS template';
      showToast(errorMessage, 'error');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      body_text: template.body_text || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await apiClient.delete(API_ENDPOINTS.SMS_TEMPLATE_BY_ID(id));
      showToast('SMS template deleted successfully', 'success');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('Failed to delete SMS template', 'error');
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SMS_TEMPLATE_PREVIEW(template.id), {
        variables: sampleData
      });

      if (response.data.success) {
        setPreviewData(response.data.data);
        setPreviewTemplate(template);
        setShowPreview(true);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      showToast('Failed to generate preview', 'error');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', body_text: '' });
    setEditingTemplate(null);
    setShowForm(false);
    setValidationErrors({});
    setValidationWarnings({});
  };

  const renderPreview = () => {
    if (!previewTemplate) return '';
    return previewTemplate.body_text || '';
  };

  const getCharacterCount = () => {
    return formData.body_text.length;
  };

  const getCharacterCountColor = () => {
    const count = getCharacterCount();
    if (count > SMS_LIMIT) return 'text-red-600';
    if (count > SMS_LIMIT * 0.9) return 'text-yellow-600';
    return 'text-gray-500';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-primary btn-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Premier Choice Branded Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4">
              <span className="text-white text-xl font-bold">PR</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold">SMS Templates</h1>
              <p className="mt-1 text-primary-100">
                Premier Choice CRM - Manage SMS templates for automated notifications and communications.
              </p>
            </div>
          </div>
          {/* {isSuperAdmin() && (
            <button
              onClick={() => setShowForm(true)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 btn-animate transition-all duration-200"
            >
              <FiPlus className="h-4 w-4" />
              <span>Add Template</span>
            </button>
          )} */}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              SMS Templates
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Build and refine SMS templates to ensure clear, timely communication with clients.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No SMS templates found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Body Preview
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Length
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {templates.map((template) => (
                    <tr key={template.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {template.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {template.body_text || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={(template.body_text || '').length > SMS_LIMIT ? 'text-red-600' : 'text-gray-500'}>
                          {(template.body_text || '').length}/{SMS_LIMIT}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(template.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handlePreview(template)}
                          className="inline-flex items-center px-2 py-1 text-white hover:text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg btn-animate transition-all duration-200 mr-2"
                          title="Preview Template"
                        >
                          <FiEye className="h-4 w-4" />
                        </button>
                        {isSuperAdmin() && (
                          <>
                            <button
                              onClick={() => handleEdit(template)}
                              className="inline-flex items-center px-2 py-1 text-white hover:text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg btn-animate transition-all duration-200 mr-2"
                              title="Edit Template"
                            >
                              <FiEdit className="h-4 w-4" />
                            </button>
                            {/* <button
                              onClick={() => setShowDeleteModal(template.id)}
                              className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200"
                              title="Delete Template"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button> */}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-lg bg-white">
            {/* Branded Modal Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 -m-5 mb-6 p-6 rounded-t-md">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">PR</span>
                  </div>
                  <h3 className="text-lg font-medium text-white">
                    {editingTemplate ? 'Edit SMS Template' : 'Add New SMS Template'}
                  </h3>
                </div>
                <button
                  onClick={resetForm}
                  className="text-white hover:text-primary-200 transition-colors"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFormData(prev => ({ ...prev, [name]: value }));

                    // Real-time validation
                    const updatedData = { ...formData, message: value };
                    const validation = validateFormData(updatedData);
                    setValidationErrors(validation.errors);
                    setValidationWarnings(validation.warnings);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 ${validationErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  required
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Body
                </label>
                <div className="mb-2">
                  <p className="text-xs text-gray-500">
                    Supported variables: {supportedVariables.join(', ')}
                  </p>
                </div>
                <textarea
                  name="body_text"
                  value={formData.body_text}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFormData(prev => ({ ...prev, [name]: value }));

                    // Real-time validation
                    const updatedData = { ...formData, [name]: value };
                    const validation = validateFormData(updatedData);
                    setValidationErrors(validation.errors);
                    setValidationWarnings(validation.warnings);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 ${validationErrors.message ? 'border-red-300' : 'border-gray-300'
                    }`}
                  rows={6}
                  placeholder="Enter your SMS message here..."
                  required
                />
                {validationErrors.message && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.message}</p>
                )}
                {validationWarnings.message && (
                  <p className="mt-1 text-sm text-yellow-600">{validationWarnings.message}</p>
                )}
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-gray-500">
                    SMS messages are typically limited to {SMS_LIMIT} characters
                  </span>
                  <span className={`text-xs font-medium ${getCharacterCountColor()}`}>
                    {getCharacterCount()}/{SMS_LIMIT}
                  </span>
                </div>
              </div>

              {validationErrors.general && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{validationErrors.general}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200 flex items-center space-x-2"
                >
                  <FiSave className="h-4 w-4" />
                  <span>{editingTemplate ? 'Update Template' : 'Add Template'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Preview: {previewTemplate.name}
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              {/* SMS Preview */}
              <div className="bg-gray-100 p-4 border-b">
                <div className="flex items-center mb-2">
                  <div className="w-6 h-6 bg-brand-primary rounded flex items-center justify-center mr-2">
                    <span className="text-white text-xs font-bold">PR</span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">Premier Choice</span>
                </div>
              </div>

              <div className="p-4 bg-white">
                <div className="bg-blue-500 text-white p-3 rounded-lg max-w-xs ml-auto">
                  <p className="text-sm whitespace-pre-wrap">{renderPreview()}</p>
                </div>
              </div>
            </div>

            {/* <div className="mt-4 text-xs text-gray-500">
              <p>Preview uses sample data: {JSON.stringify(sampleData, null, 2)}</p>
              <p className="mt-1">
                Character count: {renderPreview().length}/{SMS_LIMIT}
              </p>
            </div> */}
          </div>
        </div>
      )}

      {/* Delete Template Modal */}
      {showDeleteModal !== null && (
        <DeleteItemModal
          entity="template"
          url={API_ENDPOINTS.SMS_TEMPLATE_BY_ID(showDeleteModal)}
          isOpen={showDeleteModal !== null}
          onClose={() => setShowDeleteModal(null)}
          onItemDeleted={() => fetchTemplates()}
        />
      )}
    </div>
  );
};

export default SMSTemplates;